import React, { ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Button from '../../buttons/Button';
import WarningTriangleIcon from '../../../assets/icons/WarningTriangleIcon';

import { APP_PAGE_ORDER_LIST, PICK_TOUR_PAGE_PICK_TOUR } from '../../../constants';
import { setAppPage } from '../../../redux/actions/appPage.actions';
import { clearErrorFallbackPageData } from '../../../redux/actions/errorFallbackPage.actions';
import { getErrorFallbackPageData } from '../../../redux/selectors';

import styles from './ErrorFallback.css';
import { setPickTourPage } from '../../../redux/actions/pickTourPage.actions';

const ErrorFallback = (): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const errorFallbackData = useSelector(getErrorFallbackPageData);

  const handleReturnClick = () => {
    dispatch(setAppPage({ name: APP_PAGE_ORDER_LIST }));
    dispatch(setPickTourPage({ name: PICK_TOUR_PAGE_PICK_TOUR }));
    dispatch(clearErrorFallbackPageData({}));
  };

  // Default error messages if none provided
  const defaultMessages = [
    t('ERRORS.UNKNOWN_ERROR_LINE_1'),
    t('ERRORS.UNKNOWN_ERROR_LINE_2')
  ];

  // Convert errorMessage to array for consistent handling
  let messages: string[];
  if (errorFallbackData.errorMessage) {
    const rawMessages = Array.isArray(errorFallbackData.errorMessage)
      ? errorFallbackData.errorMessage
      : [errorFallbackData.errorMessage];

    // Translate messages if they are translation keys
    messages = rawMessages.map(msg => {
      // Check if it's a translation key (e.g., starts with common prefixes)
      if (msg && (msg.startsWith('ERRORS.') || msg.startsWith('WAFFLE_MENU.') || msg.startsWith('ACTIVE_PICK_TOUR.'))) {
        return t(msg);
      }
      return msg;
    });
  } else {
    messages = defaultMessages;
  }

  const displayButtonText = errorFallbackData.buttonText || t('WAFFLE_MENU.RETURN_TO_PENDING_ORDERS');

  const renderIcon = () => {
    if (!errorFallbackData.showIcon || !errorFallbackData.iconType) return null;

    switch (errorFallbackData.iconType) {
      case 'warning':
        return <WarningTriangleIcon />;
      case 'error':
      case 'info':
      default:
        return null;
    }
  };

  return (
    <div className={styles.errorOverlay}>
      <div className={styles.errorContent}>
        {errorFallbackData.showIcon && errorFallbackData.iconType && (
          <div className={styles.iconContainer}>
            {renderIcon()}
          </div>
        )}
        {messages.map((message) => (
          <p key={message} className={styles.errorMessage}>
            {message}
          </p>
        ))}
      </div>
      <div className={styles.buttonContainer}>
        <Button onClick={handleReturnClick} className={styles.button}>
          {displayButtonText}
        </Button>
      </div>
    </div>
  );
};

export default ErrorFallback;
